import { buildR2AssetsImageUrl } from "@saas/shared/lib/image-url-builder";
import type {
	AiAirKissQualityId,
	AiAirKissTemplateId,
} from "./ai-air-kiss-preset";

/**
 * /ai-air-kiss-video-generator 样片区的片子；第一条同时是工作台右栏唯一的 Example。
 *
 * 自产片在本页工作台用预设出，onSite true，标档位，「Try this example」回填原图并切到出片的飞吻方式。
 * 来源和压制参数记在 packages/scripts/ai-air-kiss-showcase/README.md。
 *
 * R2 `home/showcase/ai-air-kiss/` 下 clips/{id}-720p.mp4、posters/{id}-720p.jpg、refs/{ref}.jpg；
 * 右栏和样片区用同一份 720p 文件。清单为空时样片区整个不渲染。
 */
export interface AiAirKissShowcaseClip {
	id: string;
	title: string;
	/** 一句话说明，JSON-LD VideoObject.description 与 <video> aria-label 用 */
	description: string;
	/** 出这条片用的画质档（只有自产片有），拼贴 hover 时显示档位名 */
	quality?: AiAirKissQualityId;
	/** 出这条片用的飞吻方式；「Try this example」顺带切过去 */
	template?: AiAirKissTemplateId;
	orientation: "landscape" | "portrait";
	/** 站内生成的才 true；false 的不进 VideoObject，文案也不能说是本站出的 */
	onSite: boolean;
	duration: number;
	videoUrl: string;
	poster: string;
	/** 成片带音轨：拼贴右上角出静音开关 */
	hasAudio: boolean;
	/** 原图（一张人像），「Try this example」填进工作台的槽 */
	inputImages?: string[];
}

const R2_BASE = "home/showcase/ai-air-kiss";

/**
 * 视频和 poster 地址的版本参数（assets 域边缘缓存一天，连 404 也缓存）；换片子时加一
 */
const CLIP_VERSION = 1;

type Spec = Omit<
	AiAirKissShowcaseClip,
	"videoUrl" | "poster" | "inputImages"
> & {
	/** 自产片的原图（refs/ 下的文件名，或别的页面 refs 的完整 R2 路径） */
	refs?: string[];
};

/**
 * 顺序即拼贴顺序，第一条是右栏 Example。全竖版（方案：Vidu 两条 16:9 不收，混排手机上乱）。
 * 自产片 2026-10-30 本页 HD 出（Seedance 2.0 Mini 720p 5 秒 9:16，41 积分；Hearts 那条 10/31 由爷爷换成年轻女生，老公定）；
 * 外部片来自 Monet（monet.vision/video-effects/ai-air-kiss，10/30 抓，原片 832×1120 无声无水印），被投诉就撤
 */
const SPECS: Spec[] = [
	{
		id: "sweet-beach-woman",
		title: "Sweet Air Kiss on the Beach at Golden Hour",
		description:
			"One beach portrait at golden hour becomes a sweet air kiss: she smiles into the camera, kisses her fingertips and blows the kiss toward you with an open palm.",
		quality: "hd",
		template: "sweet",
		orientation: "portrait",
		onSite: true,
		duration: 5,
		hasAudio: true,
		refs: ["beach-woman"],
	},
	{
		id: "wave-city-man",
		title: "Air Kiss and Wave on a City Street at Dusk",
		description:
			"A street portrait at dusk becomes a playful air kiss: he blows a kiss to the camera, gives a little wave and breaks into a big smile.",
		quality: "hd",
		template: "wave",
		orientation: "portrait",
		onSite: true,
		duration: 5,
		hasAudio: true,
		refs: ["city-man"],
	},
	{
		id: "hearts-garden-woman",
		title: "Air Kiss with Floating Hearts in a Rose Garden",
		description:
			"A rose garden portrait becomes a flying kiss: she kisses her palm, blows it to the camera and small pink hearts drift toward the lens.",
		quality: "hd",
		template: "hearts",
		orientation: "portrait",
		onSite: true,
		duration: 5,
		hasAudio: true,
		refs: ["garden-woman"],
	},
	{
		id: "studio-blow-kiss",
		title: "Blowing a Kiss in a Studio Portrait",
		description:
			"A studio portrait of a woman blowing a kiss toward the camera, then making a heart with her hands.",
		orientation: "portrait",
		onSite: false,
		duration: 5,
		hasAudio: false,
	},
];

const refUrl = (ref: string) =>
	buildR2AssetsImageUrl(
		ref.startsWith("home/") ? ref : `${R2_BASE}/refs/${ref}.jpg`,
	);

export const aiAirKissShowcaseClips: AiAirKissShowcaseClip[] = SPECS.map(
	({ refs, ...spec }) => ({
		...spec,
		videoUrl: buildR2AssetsImageUrl(
			`${R2_BASE}/clips/${spec.id}-720p.mp4?v=${CLIP_VERSION}`,
		),
		poster: buildR2AssetsImageUrl(
			`${R2_BASE}/posters/${spec.id}-720p.jpg?v=${CLIP_VERSION}`,
		),
		inputImages: refs?.map(refUrl),
	}),
);

/** 样片上传日期，喂 VideoObject.uploadDate（完整 ISO 8601 带时区） */
export const AI_AIR_KISS_SHOWCASE_UPLOADED_AT = "2026-10-30T00:00:00+08:00";
